<script lang="ts" setup>
import { onMounted, ref, Ref, computed, WritableComputedRef, PropType } from 'vue';
import {
    ConnectFlowModelingActions,
    ConnectFlowModelingGetters,
    ConnectFlowModelingMutations,
    VueFlowNodeModel,
    MultiSelectOption,
    QueueModel,
} from '@/types';

import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { ReplyIcon } from '@heroicons/vue/outline';
import { required } from '@/utils/validators';
import { useI18n } from 'vue-i18n';
import store from '@/store';
import { helpers } from '@vuelidate/validators';

const { t } = useI18n();

const props = defineProps({
    node: {
        type: Object as PropType<VueFlowNodeModel>,
        required: true,
    },
});

const showForm = ref(false);

const nodeWritable: WritableComputedRef<VueFlowNodeModel> = computed({
    get: () => store.getters['flow/' + ConnectFlowModelingGetters.ELEMENT_BY_ID](props.node.id),
    set: (value: VueFlowNodeModel) => {
        store.commit('flow/' + ConnectFlowModelingMutations.UPDATE_ELEMENT, {
            idx: null,
            value,
        });
    },
});

const queueOptions = computed(
    () => store.getters['flow/' + ConnectFlowModelingGetters.QUEUE_OPTIONS],
);
interface ITransferFormState {
    queue: null | QueueModel;
    whisper: null | string;
    queueTimeoutSeconds: null | number;
}

const formState: Ref<ITransferFormState> = ref({
    queue: null,
    whisper: null,
    queueTimeoutSeconds: null,
});

const queueTimeoutValidationText = t('connectFlow.node.transferToQueue.queueTimeout.validation');
const validQueueTimeout = helpers.withMessage(queueTimeoutValidationText, (value) => {
    if (value === null || value === '' || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    const n = Number(value);
    return Number.isInteger(n) && n >= 5 && n <= 60;
});

const rules = computed<ValidationArgs<ITransferFormState>>(() => {
    const r: ValidationArgs<ITransferFormState> = {
        queue: {
            required,
        },
        whisper: {},
        queueTimeoutSeconds: { validQueueTimeout },
    };
    return r;
});
const v$: Ref<Validation<ValidationArgs<ITransferFormState>>> = useVuelidate(rules, formState);

const nodeText = computed(() => {
    let text = t('connectFlow.node.transferToQueue.empty');
    if (nodeWritable.value.data.related) {
        text = nodeWritable.value.data.related.label;
    } else if (nodeWritable.value.data.textToAudio) {
        text = t('connectFlow.node.transferToQueue.textToAudio');
    }
    return text;
});

const isFormCorrect = ref(true);

function removeNode() {
    store.dispatch('flow/' + ConnectFlowModelingActions.DELETE_NODE, {
        nodeId: nodeWritable.value.id,
        parentId: nodeWritable.value.parentId,
    });
}
async function onSubmit() {
    isFormCorrect.value = await v$.value.$validate();
    if (isFormCorrect.value) {
        nodeWritable.value.data.related = {
            value: formState.value.queue?.id,
            label: formState.value.queue?.name,
        } as MultiSelectOption;
        nodeWritable.value.data.whisper = formState.value.whisper ?? undefined;
        nodeWritable.value.data.queueTimeoutSeconds =
            formState.value.queueTimeoutSeconds ?? undefined;
        showForm.value = false;
    }
    nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    v$.value.$reset();
}

onMounted(() => {
    if (nodeWritable.value.data.related !== null) {
        formState.value.queue = queueOptions.value.find(
            (queue: QueueModel) => queue.id == nodeWritable.value.data.related?.value,
        );
    }

    if (nodeWritable.value.data.whisper !== undefined) {
        formState.value.whisper = nodeWritable.value.data.whisper;
    }

    if (nodeWritable.value.data.queueTimeoutSeconds !== undefined) {
        formState.value.queueTimeoutSeconds = nodeWritable.value.data.queueTimeoutSeconds;
    }

    v$.value.$validate().then((result) => {
        isFormCorrect.value = result;
        nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    });
});
</script>

<template>
    <UiNode
        :nodeId="node.id"
        :title="$t('connectFlow.node.transferToQueue.title')"
        :icon="ReplyIcon"
        showActions
        @editNode="showForm = true"
        @removeNode="removeNode"
        :class="isFormCorrect ? '' : 'border-red-300 shadow-red-300'"
    >
        <div class="font-medium text-gray-600">
            <span>
                {{ nodeText }}
            </span>
        </div>
    </UiNode>
    <UiRightPanel
        v-model="showForm"
        :title="$t('connectFlow.node.transferToQueue.title')"
        :icon="ReplyIcon"
    >
        <form autocomplete="off" class="flex flex-col gap-3" @submit.prevent="onSubmit">
            <div class="sm:col-span-12">
                <label class="mb-2 block text-sm font-medium text-gray-800">
                    {{ $t('connectFlow.node.transferToQueue.select') }}
                </label>
                <UiMultiselect
                    name="queue"
                    label="name"
                    value-prop="id"
                    v-model="v$.queue.$model"
                    :options="queueOptions"
                    :errors="v$.queue.$silentErrors"
                    can-clear
                />
            </div>
            <div class="mt-4 flex flex-col justify-end pb-1.5">
                <UiTextArea
                    :label="$t('connectFlow.node.transferToQueue.whisper')"
                    name="whisper"
                    v-model="v$.whisper.$model"
                />
            </div>
            <div class="mt-4 flex flex-col justify-end pb-1.5">
                <UiTextInput
                    type="number"
                    name="queueTimeoutSeconds"
                    :label="$t('connectFlow.node.transferToQueue.queueTimeout.label')"
                    v-model="v$.queueTimeoutSeconds.$model as unknown as string | number"
                    :errors="v$.queueTimeoutSeconds.$errors"
                    input-class="px-4 py-3"
                />
                <p class="mt-1 text-xs text-gray-500">
                    {{ $t('connectFlow.node.transferToQueue.queueTimeout.help') }}
                </p>
            </div>
            <div class="mt-6 grid grid-cols-2 place-content-around gap-4 text-center">
                <UiButton variant="white" text-variant="gray-700" @click="showForm = false">
                    {{ $t('core.actions.Cancel') }}
                </UiButton>
                <UiButton type="submit" variant="primary" text-variant="white">
                    {{ $t('core.actions.Apply') }}
                </UiButton>
            </div>
        </form>
    </UiRightPanel>
</template>
